import {
  Button, CircularProgress, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Typography, Grid,
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ClearIcon from '@material-ui/icons/Clear';
import TableChartOutlinedIcon from '@material-ui/icons/TableChartOutlined';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { composeWhereClause, displayFacilityType, displayFamilyType } from '../../modules/data';
import { rdsSelect, rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toCurrency, toPercent } from '../../modules/text';
import { getMedianHouseholdIncome, getRatesByState } from './data';

function CountyComparisonTable() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [stateData, setStateData] = useState(null);
  const { familyType, settingType, childAge } = useContext(FiltersContext);
  const { primaryColor } = useContext(StoryContext);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCounties, setSelectedCounties] = useState([]);

  const [incomeAttribute, setIncomeAttribute] = useState('inc_hh');

  useEffect(() => {
    // console.log(`family type changed to ${familyType}. Recalculating...`);
    if (familyType === 'All') {
      setIncomeAttribute('inc_hh');
    }
    if (familyType === 'Married-couple family') {
      setIncomeAttribute('inc_fam_married');
    }

    if (familyType === 'Single-parent family') {
      setIncomeAttribute('inc_fam_nospouse');
    }
  }, [familyType]);

  useEffect(() => {
    function getRatesByCounty() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_setting_group_rate_annual', {
        dims: 'county_fips',
        measure: 'average_rate:AVG(rate)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
        orderby: 'county_fips',
      }, true);
    }

    // function getRatesByState() {
    //   let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
    //   whereClause += composeWhereClause({ settingType, childAge });

    //   return rdsTabulate('ccdc', 'county_setting_group_rate_annual', {
    //     dims: 'state',
    //     measure: 'average_rate:AVG(rate)',
    //     where: whereClause,
    //     format: 'amcharts',
    //     inject: true,
    //   }, true);
    // }

    // function getMedianHouseholdIncome() {
    //   return rdsSelect('3rdparty', 'acs_sams_state_wide', {
    //     cols: 'inc_hh,inc_fam_married,inc_fam_nospouse',
    //     where: `state_fips=${stateFIPS[stateCode.toUpperCase()]}`,
    //     format: 'amcharts',
    //     inject: false,
    //   }, true);
    // }

    function getCountyMedianHouseholdIncome() {
      return rdsSelect('3rdparty', 'acs_sams_county_wide', {
        cols: 'county_fips,inc_hh,inc_fam_married,inc_fam_nospouse',
        where: `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND (county_suppressed=0 OR county_suppressed=NULL)`,
        format: 'amcharts',
        inject: true,
        orderby: 'county_fips',
      });
    }

    // need data for state rate, county income, state income
    if (stateCode && states[stateCode.toUpperCase()]) {
      setReady(false);
      Promise.all([getRatesByCounty(), getRatesByState(stateCode, null, { settingType, childAge }), getCountyMedianHouseholdIncome(), getMedianHouseholdIncome(stateCode)])
        .then((responses) => {
          const d = [];
          const countyRates = responses[0].parsedBody.dataProvider;
          const stateRate = responses[1].parsedBody.dataProvider[0];
          const countyIncomes = responses[2].parsedBody.dataProvider;
          const stateIncomes = responses[3].parsedBody.dataProvider[0];

          setStateData({ ...stateRate, ...stateIncomes });

          countyRates.forEach((rate) => {
            const countyIncome = _.find(countyIncomes, { county_fips: rate.county_fips });
            const dataPoint = { ...rate, ...countyIncome }; // merge rate and income data

            if (dataPoint[incomeAttribute]) {
              dataPoint.value = dataPoint.average_rate / dataPoint[incomeAttribute]; // value used by map to color
            }
            d.push(dataPoint);
          });
          // realign data. Update selected counties with fresh data
          const fips = selectedCounties.map((c) => (c.county_fips));
          if (fips.length > 0) {
            setSelectedCounties(_.filter(d, (o) => fips.includes(o.county_fips)));
          }
          // set data
          setData(d);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setReady(true);
        });
    }
  }, [stateCode, settingType, childAge]);

  useEffect(() => {
    // console.log(`income attribute changed to ${incomeAttribute}. Recalculating...`);
    const dr = [];
    const d = data.map((dataPoint) => {
      const dp = { ...dataPoint };
      if (dp[incomeAttribute]) {
        dp.value = dp.average_rate / dp[incomeAttribute];
        dr.push(dp.value);
      }
      return dp;
    });
    setData(d);
  }, [incomeAttribute]);

  const handleSelectionChange = (event) => {
    setSelectedCounty(event.target.value);
  };

  const handleAddCounty = () => {
    if (!_.includes(selectedCounties, selectedCounty)) {
      setSelectedCounties(selectedCounties.concat([selectedCounty]));
    }
    setSelectedCounty('');
  };

  const removeCounty = (c) => () => {
    if (!c) return;
    setSelectedCounties(selectedCounties.filter((f) => (f.county_fips !== c.county_fips)));
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: primaryColor,
      }}
      >
        <TableChartOutlinedIcon />&nbsp;<Typography variant="h5">County Comparison Table</Typography>
      </div>
      <Grid container>
        <Grid item xs={8} sm={6} lg={3}>
          <FormControl>
            <InputLabel id="countySelect">County</InputLabel>
            <Select
              labelId="countySelect"
              id="countySelectHelper"
              value={selectedCounty}
              onChange={handleSelectionChange}
            >
              {data.map((d) => (<MenuItem value={d} key={d.county_fips}>{d.county_fips}</MenuItem>))}

            </Select>
            <FormHelperText>Select a county from the menu to add to the comparison table</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={6} lg={9}>
          <Button
            disabled={!selectedCounty}
            variant="contained"
            onClick={handleAddCounty}
            style={{
              marginLeft: '20px', marginBottom: '-70px', backgroundColor: primaryColor, color: '#fff',
            }}
            size="small"
          >Add to table
          </Button>
        </Grid>
      </Grid>

      <Table size="small" aria-label="County comparison table" style={{ overflowX: 'auto', display: 'block' }}>
        <TableHead>
          <TableRow>
            <TableCell>County</TableCell>
            <TableCell align="right">Median monthly price of {displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</TableCell>
            <TableCell align="right">Median monthly income for a {displayFamilyType(familyType).toLowerCase()}</TableCell>
            <TableCell align="right">Price as percentage of median household income</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow selected>
            <TableCell component="th" scope="row">
              {stateData.state} (State)
            </TableCell>
            <TableCell align="right">{toCurrency((stateData.average_rate) / 12)}</TableCell>
            <TableCell align="right">{toCurrency((stateData[incomeAttribute]) / 12)}</TableCell>
            <TableCell align="right">{toPercent(stateData.average_rate / stateData[incomeAttribute])}</TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {selectedCounties.map((row) => (
            <TableRow key={row.county_fips}>
              <TableCell component="th" scope="row">
                {row.county_fips}
              </TableCell>
              <TableCell align="right">{toCurrency((row.average_rate) / 12)}</TableCell>
              <TableCell align="right">{row[incomeAttribute] ? toCurrency((row[incomeAttribute]) / 12) : 'No Data'}</TableCell>
              <TableCell align="right">{row[incomeAttribute] ? toPercent(row.average_rate / row[incomeAttribute]) : 'No Data'}</TableCell>
              <TableCell><IconButton size="small" onClick={removeCounty(row)} aria-label={`Remove ${row.county_fips}`}><ClearIcon /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export default CountyComparisonTable;
